import React from 'react';
import { PropTypes } from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Map, Marker } from 'react-map-gl/mapbox';
import 'mapbox-gl/dist/mapbox-gl.css';
import { withStyles } from '@mui/styles';
import {
  Card,
  IconButton,
  Tooltip,
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';

const mapboxToken =
  'pk.eyJ1IjoiZG9jdGl2aXR5IiwiYSI6ImNsMmo2OWx6bTBtdmUza3J6eDQ2Z3pvdG0ifQ.o9j6xJxOa2WKQLYdqQaJRQ';

const styles = (theme) => ({
  card: {
    marginTop: theme.spacing(2),
    overflow: 'hidden',
    cursor: 'pointer',
    position: 'relative',
    '&:hover $expandIcon': {
      opacity: 1,
    },
  },
  cardNonClickable: {
    marginTop: theme.spacing(2),
    overflow: 'hidden',
    cursor: 'default',
    position: 'relative',
  },
  marker: {
    color: theme.palette.primary.main,
    filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))',
  },
  expandIcon: {
    position: 'absolute',
    top: 8,
    right: 8,
    backgroundColor: 'rgba(255,255,255,0.9)',
    opacity: 0,
    transition: 'opacity 0.2s',
    zIndex: 1,
    '&:hover': {
      backgroundColor: 'rgba(255,255,255,1)',
    },
  },
});

/**
 * LocationMapLink - A small map preview card that navigates to the Locations Map tab
 * 
 * Usage in ProviderDetailPage or other pages:
 * 
 * <LocationMapLink
 *   location={provider.location}
 * />
 * 
 * Or with explicit coordinates:
 * 
 * <LocationMapLink
 *   latitude={40.4416}
 *   longitude={-79.9565}
 *   address1="123 Main St"
 *   city="Pittsburgh"
 *   state="PA"
 *   postalCode="15213"
 *   locationId={123}
 * />
 */
function LocationMapLink(props) {
  const {
    classes,
    location, // Can pass entire location object
    latitude: explicitLat,
    longitude: explicitLng,
    address1: explicitAddress1,
    city: explicitCity,
    state: explicitState,
    postalCode: explicitPostalCode,
    locationId: explicitLocationId,
    zoom = 14,
  } = props;

  const navigate = useNavigate();
  const user = useSelector((state) => state.user);
  const isAdmin = user?.is_admin;

  // Extract coordinates and address from location object or explicit props
  let latitude, longitude, address1, city, state, postalCode, locationId;
  
  if (location) {
    // Handle both point object and individual lat/lng fields
    if (location.point?.coordinates) {
      // Sequelize serializes to standard GeoJSON: [longitude, latitude]
      longitude = location.point.coordinates[0];
      latitude = location.point.coordinates[1];
    } else if (location.latitude !== undefined && location.longitude !== undefined) {
      latitude = parseFloat(location.latitude);
      longitude = parseFloat(location.longitude);
    }
    address1 = location.address1;
    city = location.city;
    state = location.state;
    postalCode = location.postal_code;
    locationId = location.id;
  } else {
    latitude = explicitLat;
    longitude = explicitLng;
    address1 = explicitAddress1;
    city = explicitCity;
    state = explicitState;
    postalCode = explicitPostalCode;
    locationId = explicitLocationId;
  }

  // Don't render if no coordinates
  if (!latitude || !longitude) {
    return null;
  }

  // Parse coordinates to ensure they're numbers
  const lat = parseFloat(latitude);
  const lng = parseFloat(longitude);

  // Validate coordinates
  if (isNaN(lat) || isNaN(lng) || lat < -90 || lat > 90 || lng < -180 || lng > 180) {
    return null;
  }

  const formatAddress = () => {
    const parts = [address1, city, state, postalCode].filter(Boolean);
    return parts.join(', ');
  };

  const handleClick = () => {
    // Build URL with query params for the Map tab
    const params = new URLSearchParams();
    params.set('lat', lat.toString());
    params.set('lng', lng.toString());
    
    const addressStr = formatAddress();
    if (addressStr) {
      params.set('address', addressStr);
    }
    if (locationId) {
      params.set('locationId', locationId.toString());
    }
    
    navigate(`/locations_map/?${params.toString()}`);
  };

  const tooltipTitle = isAdmin ? "View on map" : "Location";

  return (
    <Tooltip title={tooltipTitle} arrow>
      <Card 
        className={isAdmin ? classes.card : classes.cardNonClickable} 
        onClick={isAdmin ? handleClick : undefined}
      >
        <Map
          mapLib={import('mapbox-gl')}
          initialViewState={{
            longitude: lng,
            latitude: lat,
            zoom: zoom,
          }}
          style={{
            width: '100%',
            height: 180,
          }}
          mapStyle="mapbox://styles/mapbox/light-v10"
          mapboxAccessToken={mapboxToken}
          attributionControl={false}
          interactive={false}
        >
          <Marker longitude={lng} latitude={lat} anchor="bottom">
            <LocationOnIcon
              className={classes.marker}
              style={{ fontSize: 36 }}
            />
          </Marker>
        </Map>
        {isAdmin && (
          <IconButton className={classes.expandIcon} size="small">
            <OpenInFullIcon fontSize="small" />
          </IconButton>
        )}
      </Card>
    </Tooltip>
  );
}

LocationMapLink.propTypes = {
  classes: PropTypes.object.isRequired,
  location: PropTypes.shape({
    id: PropTypes.number,
    point: PropTypes.shape({
      coordinates: PropTypes.array,
    }),
    latitude: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    longitude: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    address1: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    postal_code: PropTypes.string,
  }),
  latitude: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  longitude: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  address1: PropTypes.string,
  city: PropTypes.string,
  state: PropTypes.string,
  postalCode: PropTypes.string,
  locationId: PropTypes.number,
  zoom: PropTypes.number,
};

export default withStyles(styles)(LocationMapLink);